import { formatAmountInPounds } from "util/formatNumber";

/** Extract just the date portion (YYYY-MM-DD) from an ISO string. */
export function formatDateOnly(value: string | null | undefined): string {
  if (value == null) return "—";
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : value;
}

/** Fields whose values are dates; shown as YYYY-MM-DD wherever a value renders. */
const DATE_FIELDS: ReadonlySet<string> = new Set(["shippedDate"]);

/**
 * Strip JSON-ish quoting/bracket artifacts from a stored string value so it
 * renders cleanly. Stored values sometimes look like `"D001"` or
 * `["D001", "D002"]`; we want `D001` and `D001, D002` respectively.
 * `null` and `"[]"` render as an em dash placeholder.
 */
export function formatDisplayValue(value: string | null | undefined): string {
  if (value == null) return "—";
  if (value === "[]") return "—";
  const stripped = value.replace(/^"(.*)"$/, "$1").replace(/"\s*,\s*"/g, ", ");
  const unbracketed = stripped.replace(/^\[(.*)\]$/, "$1").replace(/"/g, "");
  return unbracketed || "—";
}

/** Quantity is stored in pounds at full float precision; show at most two decimals, with the unit. */
function formatQuantityValue(value: string | null | undefined): string {
  // ast-grep-ignore: no-unsafe-number-parsing
  const pounds = value == null ? Number.NaN : Number(value);
  if (!Number.isFinite(pounds)) return formatDisplayValue(value);
  // formatAmountInPounds leaves 0 blank, and two decimals would show 0.001 as "0".
  if (pounds === 0) return "0 lbs";
  if (Math.abs(pounds) < 0.01) return `${pounds} lbs`;
  return formatAmountInPounds(pounds);
}

/** formatDisplayValue, except ship date renders as YYYY-MM-DD and quantity is rounded and in lbs. */
export function formatFieldValue(field: string, value: string | null | undefined): string {
  if (DATE_FIELDS.has(field)) return formatDateOnly(value);
  if (field === "quantity") return formatQuantityValue(value);
  return formatDisplayValue(value);
}
